/**
 * The in-app announcements (PLAN §13 phase 7b item 5): what the vendor has in
 * force for the caller's tenant (`GET /announcements`), for the banner above
 * every page. Live through the `announcement` change kind (`changes.ts`
 * invalidates `['announcements']`).
 *
 * Cloud only: a node serves no such route, so the query is disabled there by
 * the hook (`enabled`), never fetched and failed.
 */
import type { ApiClient } from '@acp/api-client/client.js';
import type { TenantAnnouncementWire } from '@acp/api-contract/vendor.js';
import { queryOptions } from '@tanstack/react-query';

export const announcementKeys = {
  all: ['announcements'] as const,
};

/**
 * How often the banner re-reads: an announcement's start or end passing is time, not a write anyone hears on the
 * change channel, so a scheduled notice appears (and an ended one goes) within a minute in an open tab.
 */
export const ANNOUNCEMENTS_POLL_MS = 60_000;

/** In force for my tenant, the most severe first, then the newest. */
export function announcementsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: announcementKeys.all,
    refetchInterval: ANNOUNCEMENTS_POLL_MS,
    queryFn: async (): Promise<TenantAnnouncementWire[]> => {
      const res = await api.call('listAnnouncements', { params: null, query: null, body: null });
      return [...res.announcements].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.startsAt.localeCompare(a.startsAt));
    },
  });
}

// `unknown`: a severity from a newer cloud — ranked as minor, above plain information.
const SEVERITY_RANK: Readonly<Record<TenantAnnouncementWire['severity'], number>> = { info: 0, unknown: 1, minor: 1, major: 2, critical: 3 };
